import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { hubAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Activity,
    ChevronDown,
    ChevronUp,
    MessageSquare,
    ArrowRight,
    CheckCircle2,
    Trophy,
    RotateCcw,
    Sparkles,
    Lightbulb,
    Paperclip,
    FolderPlus,
    Loader2,
    RefreshCw,
    AtSign,
} from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Fil d'activité unifié : messages, changements d'étape, tâches, clôtures, idées.
 * Cliquer sur un élément → ouvre le projet correspondant.
 * "Tout marquer comme lu" → efface le badge global sur le menu Projets.
 */
export function ActivityFeed() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(() => {
        try { return localStorage.getItem('valnexo_hub_activity_expanded') !== '0'; } catch { return true; }
    });
    const [markingSeen, setMarkingSeen] = useState(false);
    const seenTriggeredRef = useRef(false);

    const load = useCallback(async () => {
        try {
            const r = await hubAPI.recentActivity(25);
            const data = r.data;
            setItems(Array.isArray(data) ? data : (data.items || []));
        } catch {
            /* silencieux — le fil peut échouer sans casser la page */
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    // Auto-refresh toutes les 45s pour capter les nouveautés
    useEffect(() => {
        const t = setInterval(load, 45000);
        return () => clearInterval(t);
    }, [load]);

    const toggle = () => {
        setExpanded((v) => {
            const nv = !v;
            try { localStorage.setItem('valnexo_hub_activity_expanded', nv ? '1' : '0'); } catch { /* noop */ }
            return nv;
        });
    };

    const unreadCount = useMemo(() => items.filter((i) => i.unread).length, [items]);

    const markAllSeen = async () => {
        if (markingSeen) return;
        setMarkingSeen(true);
        try {
            await hubAPI.markActivitySeen();
            setItems((prev) => prev.map((i) => ({ ...i, unread: false })));
            // Signal aux badges globaux (Sidebar / MobileNav) qu'ils doivent se rafraîchir
            try { window.dispatchEvent(new Event('hub:activity-seen')); } catch { /* noop */ }
        } catch { /* silencieux */ } finally {
            setMarkingSeen(false);
        }
    };

    // Marquage automatique après 4s de consultation (si le fil est ouvert et contient des non-lus)
    useEffect(() => {
        if (!expanded || unreadCount === 0 || seenTriggeredRef.current) return;
        const t = setTimeout(() => {
            seenTriggeredRef.current = true;
            markAllSeen();
        }, 4000);
        return () => clearTimeout(t);
    }, [expanded, unreadCount]);

    const handleClick = (it) => {
        // Marque immédiatement la ligne comme lue localement + informe les badges globaux
        setItems((prev) => prev.map((x) => (x === it ? { ...x, unread: false } : x)));
        // Si l'item pointe vers un projet, on marque ce projet comme visité côté serveur
        // → la pastille rouge du menu "Projets" se met à jour dès le prochain poll (45s max)
        //   ou instantanément via l'event `hub:activity-seen`.
        try {
            const match = it.link && it.link.match(/\/projets\/projet\/([^/?#]+)/);
            if (match?.[1]) {
                hubAPI.markProjectVisited(match[1])
                    .then(() => { try { window.dispatchEvent(new Event('hub:activity-seen')); } catch { /* noop */ } })
                    .catch(() => { /* silencieux */ });
            }
        } catch { /* noop */ }
        if (it.link) {
            // Naviguer côté SPA
            if (it.link.startsWith('/projets')) {
                navigate(it.link);
            } else {
                window.location.href = it.link;
            }
        }
    };

    return (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="activity-feed">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-indigo-50/50 to-transparent">
                <button
                    type="button"
                    onClick={toggle}
                    className="flex items-center gap-2 group text-left"
                    data-testid="activity-toggle"
                >
                    <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <Activity className="w-4 h-4 text-indigo-600" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                            Fil d&apos;activité
                            {unreadCount > 0 && (
                                <Badge className="bg-red-500 hover:bg-red-500 text-white text-[10px] px-1.5 py-0" data-testid="activity-unread-badge">
                                    {unreadCount > 20 ? '20+' : unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}
                                </Badge>
                            )}
                        </div>
                        <div className="text-[11px] text-slate-500">
                            Tout ce qui bouge sur les projets (étapes, messages, tâches, idées) — clique pour ouvrir.
                        </div>
                    </div>
                </button>
                <div className="flex items-center gap-1">
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 px-2 text-xs text-slate-500"
                        onClick={load}
                        data-testid="activity-refresh"
                        title="Rafraîchir"
                    >
                        <RefreshCw className={cn('w-3.5 h-3.5', loading && 'animate-spin')} />
                    </Button>
                    {unreadCount > 0 && (
                        <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 px-2 text-xs text-indigo-600 hover:text-indigo-700"
                            onClick={markAllSeen}
                            disabled={markingSeen}
                            data-testid="activity-mark-seen"
                        >
                            {markingSeen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Tout marquer lu'}
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-slate-400"
                        onClick={toggle}
                        aria-label={expanded ? 'Replier' : 'Déplier'}
                    >
                        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </Button>
                </div>
            </div>
            {expanded && (
                <div className="max-h-[420px] overflow-y-auto">
                    {loading && items.length === 0 && (
                        <div className="p-6 text-center text-sm text-slate-400" data-testid="activity-loading">
                            <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
                        </div>
                    )}
                    {!loading && items.length === 0 && (
                        <div className="p-6 text-center text-sm text-slate-400" data-testid="activity-empty">
                            Aucune activité récente. Poste un message ou avance une étape pour animer le mur !
                        </div>
                    )}
                    {items.map((it, idx) => (
                        <ActivityItem key={`${it.type}-${idx}-${it.created_at}`} item={it} onClick={() => handleClick(it)} />
                    ))}
                </div>
            )}
        </div>
    );
}

function ActivityItem({ item, onClick }) {
    const { icon: Icon, color, bg, label } = describeItem(item);
    const author = item.author;
    const authorName = author ? `${author.first_name || ''} ${author.last_name || ''}`.trim() || 'Utilisateur' : 'Système';
    const initials = author ? `${(author.first_name || '?')[0] || ''}${(author.last_name || '')[0] || ''}`.toUpperCase() : '?';
    const timeAgo = formatRelative(item.created_at);

    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'w-full text-left px-4 py-3 flex items-start gap-3 border-b border-slate-50 hover:bg-slate-50 transition-colors',
                item.unread && 'bg-indigo-50/40 hover:bg-indigo-50/60',
                item.mentions_me && 'bg-amber-50/60 hover:bg-amber-100/60 ring-1 ring-inset ring-amber-200',
            )}
            data-testid={`activity-item-${item.type}`}
        >
            {/* Avatar / icône typologique */}
            <div className="relative shrink-0">
                <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-[11px] font-semibold text-slate-600">
                    {initials}
                </div>
                <div className={cn('absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full flex items-center justify-center ring-2 ring-white', bg)}>
                    <Icon className={cn('w-2.5 h-2.5', color)} />
                </div>
            </div>
            <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-800 flex items-center gap-2 flex-wrap">
                    <span className="font-medium">{authorName}</span>{' '}
                    <span className="text-slate-500">{label}</span>{' '}
                    {item.project_name && (
                        <span className="font-medium text-slate-800">« {item.project_name} »</span>
                    )}
                    {item.mentions_me && (
                        <span
                            className="inline-flex items-center gap-1 bg-amber-500 text-white text-[9px] font-bold uppercase tracking-wide rounded-full px-2 py-0.5 shadow-sm"
                            data-testid={`activity-mention-badge-${item.type}`}
                            title="Vous êtes mentionné"
                        >
                            <AtSign className="w-3 h-3" /> Mention
                        </span>
                    )}
                </div>
                {renderExtra(item)}
                <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-400">
                    <span>{timeAgo}</span>
                    {item.has_attachments && (
                        <>
                            <span>•</span>
                            <span className="flex items-center gap-0.5"><Paperclip className="w-3 h-3" /> pièce jointe</span>
                        </>
                    )}
                    {item.unread && (
                        <>
                            <span>•</span>
                            <span className="text-red-500 font-medium">Nouveau</span>
                        </>
                    )}
                </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-300 shrink-0 mt-1" />
        </button>
    );
}

function describeItem(it) {
    switch (it.type) {
        case 'project_wall':
            return { icon: MessageSquare, color: 'text-white', bg: 'bg-blue-500', label: 'a écrit sur le mur' };
        case 'stage_moved': {
            const to = it.meta?.to_stage_name;
            return { icon: ArrowRight, color: 'text-white', bg: 'bg-indigo-500', label: to ? `a fait passer l'étape à « ${to} »` : 'a fait avancer une étape' };
        }
        case 'task_created':
            return { icon: FolderPlus, color: 'text-white', bg: 'bg-amber-500', label: 'a créé une tâche' };
        case 'task_done':
            return { icon: CheckCircle2, color: 'text-white', bg: 'bg-emerald-500', label: 'a terminé une tâche' };
        case 'project_closed':
            return { icon: Trophy, color: 'text-white', bg: 'bg-purple-500', label: it.meta?.status === 'won' ? 'a gagné le projet' : 'a clôturé le projet' };
        case 'project_reopened':
            return { icon: RotateCcw, color: 'text-white', bg: 'bg-slate-500', label: 'a rouvert le projet' };
        case 'project_created':
            return { icon: Sparkles, color: 'text-white', bg: 'bg-teal-500', label: 'a lancé le projet' };
        case 'idea':
            return { icon: Lightbulb, color: 'text-white', bg: 'bg-yellow-500', label: 'a proposé sur le mur d\u2019idées' };
        case 'comment_mention':
            return { icon: AtSign, color: 'text-white', bg: 'bg-amber-500', label: 'vous a mentionné dans un commentaire du projet' };
        case 'idea_comment_mention':
            return { icon: AtSign, color: 'text-white', bg: 'bg-amber-500', label: 'vous a mentionné dans un commentaire d\u2019idée' };
        default:
            return { icon: Activity, color: 'text-white', bg: 'bg-slate-500', label: 'a fait une action' };
    }
}

function renderExtra(it) {
    if (it.type === 'project_wall' || it.type === 'idea' || it.type === 'comment_mention' || it.type === 'idea_comment_mention') {
        if (it.preview) {
            return <div className="text-[13px] text-slate-500 mt-0.5 line-clamp-2 italic">« {it.preview} »</div>;
        }
    }
    if (it.type === 'task_created' && it.meta?.task_name) {
        return <div className="text-[13px] text-slate-500 mt-0.5 line-clamp-1">Tâche : <span className="text-slate-700">{it.meta.task_name}</span></div>;
    }
    if (it.type === 'task_done' && it.meta?.task_name) {
        return <div className="text-[13px] text-slate-500 mt-0.5 line-clamp-1">Tâche : <span className="text-slate-700 line-through">{it.meta.task_name}</span></div>;
    }
    if (it.type === 'stage_moved' && it.meta?.from_stage_name && it.meta?.to_stage_name) {
        return (
            <div className="text-[12px] text-slate-500 mt-0.5">
                {it.meta.from_stage_name} <ArrowRight className="w-3 h-3 inline mx-1" /> {it.meta.to_stage_name}
            </div>
        );
    }
    return null;
}

function formatRelative(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const now = new Date();
        const diffMs = now - d;
        const diffSec = Math.floor(diffMs / 1000);
        if (diffSec < 60) return 'à l\u2019instant';
        const diffMin = Math.floor(diffSec / 60);
        if (diffMin < 60) return `il y a ${diffMin} min`;
        const diffHr = Math.floor(diffMin / 60);
        if (diffHr < 24) return `il y a ${diffHr} h`;
        const diffDay = Math.floor(diffHr / 24);
        if (diffDay < 7) return `il y a ${diffDay} j`;
        return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
    } catch {
        return '';
    }
}
